import React, { useEffect, useState } from 'react';
import { authService, AuthUser } from '../services/authService';
import { getLoginRedirectUrl } from '../services/config';
import { processAuthenticationReturn, isReturningFromLogin } from '../services/authUtils';
import { useAppIdentity } from '../hooks/useAppIdentity';
import { isRunningStandalone } from '../utils/evoDetection';

export interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (accessGroupToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const identity = useAppIdentity();
  const standalone = isRunningStandalone();

  // In standalone mode, derive user from identity if available
  // This ensures the user state is set when identity-react authenticates
  const effectiveUser = standalone && identity.isAuthenticated && identity.user
    ? {
        email: identity.user.email || 'unknown@accessgroup.com',
        isAdmin: false,
        allowedProducts: [],
      }
    : user;

  const isAuthenticated = !!effectiveUser;

  const login = async (accessGroupToken: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const result = await authService.loginWithToken(accessGroupToken);
      if (result.success && result.user) {
        setUser(result.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // In standalone mode, authentication is handled entirely by @workspace/identity-react
        // We don't need to call authService.initialize() which would trigger /api/auth/token
        if (standalone) {
          setIsLoading(false);
          return;
        }
        
        // First check if we're returning from Access Group login (Evo tab mode only)
        if (isReturningFromLogin()) {
          const urlToken = await processAuthenticationReturn();
          
          if (urlToken) {
            const loginResult = await login(urlToken);
            if (loginResult) {
              return; // Exit early, user is now authenticated
            } else {
              console.warn('Failed to authenticate with URL token');
            }
          }
        }
        
        // Normal initialisation - check for existing session (Evo tab mode only)
        await authService.initialize();
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array is intentional for initialization

  // In standalone mode with identity auth, we don't need to wait for authService
  const effectiveIsLoading = standalone && identity.isAuthenticated
    ? false
    : isLoading;

  const value: AuthContextType = {
    user: effectiveUser,
    isLoading: effectiveIsLoading,
    isAuthenticated,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Login component for Access Group authentication
 * This is a placeholder that should be integrated with your actual Access Group auth flow
 */
export const LoginComponent: React.FC = () => {
  const { isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleAccessGroupLogin = async () => {
    try {
      setError(null);
      
      // Redirect to Access Group login page
      const redirectUrl = getLoginRedirectUrl();
      
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Authentication failed');
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      padding: '2rem',
      maxWidth: '400px',
      margin: '0 auto',
      marginTop: '2rem'
    }}>
      <h2>Feedback Dashboard Login</h2>
      <p style={{ textAlign: 'center', marginBottom: '2rem' }}>
        Please authenticate with Access Group to access the feedback dashboard.
      </p>
      
      {error && (
        <div style={{ 
          color: 'red', 
          marginBottom: '1rem', 
          padding: '0.5rem',
          border: '1px solid red',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}
      
      <button
        onClick={handleAccessGroupLogin}
        disabled={isLoading}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          fontWeight: '500',
          backgroundColor: '#E11D48', // Pink/red color matching the image
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          minWidth: '250px',
        }}
        onMouseOver={(e) => {
          if (!isLoading) {
            (e.target as HTMLButtonElement).style.backgroundColor = '#BE185D';
          }
        }}
        onMouseOut={(e) => {
          if (!isLoading) {
            (e.target as HTMLButtonElement).style.backgroundColor = '#E11D48';
          }
        }}
      >
        {/* Access Group Logo */}
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="currentColor"
          style={{ flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
        </svg>
        {isLoading ? 'Authenticating...' : 'Sign in with access credentials'}
      </button>
      
      <div style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>
        <h3>Security Features:</h3>
        <ul style={{ textAlign: 'left' }}>
          <li>✅ HttpOnly cookies for token storage</li>
          <li>✅ XSS protection</li>
          <li>✅ Automatic token refresh</li>
          <li>✅ Secure session management</li>
        </ul>
      </div>
    </div>
  );
};
